import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { QueryStatus, authAxios } from "../../../utils";
import { RootState } from "../../../redux/store";
import { fakeElectricCharges } from "./electricChargingFakeData";
import { ElectricCharge } from "./electricChargingAPI";

export interface ElectricChargingState {
  electricChargesStatus: QueryStatus;
  electricCharges: ElectricCharge[];
  reimburseChargesStatus: QueryStatus;
}

const initialState: ElectricChargingState = {
  electricChargesStatus: "idle",
  electricCharges: [],
  reimburseChargesStatus: "idle",
};

// Simulates the future backend endpoint returning the MERGED electric charges
// (CasaWatt home + our EMSP CDRs + card charges), unified format — until the API is wired.
// Only authorized charges are returned (authorizationStatus === "ALLOWED"), so the
// authorization status is not displayed in the table.
export const getElectricChargesAsync = createAsyncThunk(
  "getElectricChargesAsync/call",
  () => {
    return fakeElectricCharges;
  },
);

export const reimburseChargesAsync = createAsyncThunk(
  "reimburseCharges/call",
  async (chargeUuids: string[], thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    if (!company) throw new Error("Company not selected");
    const axios = authAxios({ companyUuid: company.uuid });
    await axios.post(`electric_charges/reimburse`, { chargeUuids });
  },
);

export const electricChargingSlice = createSlice({
  name: "electricCharging",
  initialState,
  reducers: {
    resetElectricChargesStatus(state) {
      state.electricChargesStatus = "idle";
    },
    resetReimburseChargesStatus(state) {
      state.reimburseChargesStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getElectricChargesAsync.pending, (state) => {
        state.electricChargesStatus = "processing";
      })
      .addCase(getElectricChargesAsync.fulfilled, (state, action) => {
        state.electricChargesStatus = "success";
        state.electricCharges = action.payload;
      })
      .addCase(getElectricChargesAsync.rejected, (state) => {
        state.electricChargesStatus = "failed";
      })
      .addCase(reimburseChargesAsync.pending, (state) => {
        state.reimburseChargesStatus = "processing";
      })
      .addCase(reimburseChargesAsync.fulfilled, (state) => {
        state.reimburseChargesStatus = "success";
      })
      .addCase(reimburseChargesAsync.rejected, (state) => {
        state.reimburseChargesStatus = "failed";
      });
  },
});

export const selectElectricChargesStatus = (state: RootState) =>
  state.electricCharging.electricChargesStatus;

export const selectElectricCharges = (state: RootState) =>
  state.electricCharging.electricCharges;

export const selectReimburseChargesStatus = (state: RootState) =>
  state.electricCharging.reimburseChargesStatus;

export default electricChargingSlice.reducer;
